import { useEffect, useState } from 'react';
import { sanitizeValue } from '../../lib/sanitize';
import { useFetch } from '../useFetch';
import { useOperator } from '../../contexts/OperatorContext';
import { useSort } from '../useSort';
import { BASE_URL_MAP } from '../../lib/environment';

type Action =
  | ''
  | 'addRoomType'
  | 'getRoomType'
  | 'getRoomTypes'
  | 'updateRoomType';

export const useRoomTypes = () => {
  const [action, setAction] = useState<Action>('');
  const [roomType, setRoomType] = useState<RoomType | null>(null);
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const { loading, makeRequest, response } = useFetch();
  const {
    id: operatorId,
    roomTypes: operatorRoomTypes,
    updateOperatorState,
  } = useOperator();
  const { sortAsc } = useSort<{ name: string }>('name');
  const BASE_URL = `${BASE_URL_MAP['Hospitality']}/${operatorId}/RoomTypes`;

  useEffect(() => {
    resetState();
  }, [operatorId]);

  useEffect(() => {
    if (response && !loading) {
      if (action === 'updateRoomType') {
        const mappedRoomType = mapRoomType(response);
        updateOperatorState(
          'roomTypes',
          [
            mappedRoomType,
            ...(operatorRoomTypes ?? []).filter(
              (roomType) => roomType.id !== mappedRoomType.id
            ),
          ].sort(sortAsc)
        );
      } else if (action === 'addRoomType') {
        const mappedRoomType = mapRoomType(response);

        updateOperatorState(
          'roomTypes',
          [mappedRoomType, ...(operatorRoomTypes ?? [])].sort(sortAsc)
        );
      } else if (action === 'getRoomType') setRoomType(mapRoomType(response));
      else if (action === 'getRoomTypes')
        setRoomTypes(
          response.map((roomType: ApiRoomType) => mapRoomType(roomType))
        );
    }
    // eslint-disable-next-line
  }, [loading, response]);

  const addRoomType = (roomType: CreateRoomType) => {
    setAction('addRoomType');
    const body = mapApiRoomType(roomType);

    makeRequest(BASE_URL, 'post', sanitizeValue(body));
  };

  const getRoomType = (roomTypeId: string) => {
    setAction('getRoomType');
    makeRequest(`${BASE_URL}/${roomTypeId}`, 'get');
  };

  const getRoomTypes = () => {
    setAction('getRoomTypes');
    makeRequest(BASE_URL, 'get');
  };

  const updateRoomType = (roomType: RoomType) => {
    setAction('updateRoomType');
    const body: ApiCreateRoomType = sanitizeValue({
      Active: roomType.active,
      Name: roomType.name,
    });

    makeRequest(`${BASE_URL}/${roomType.id}`, 'put', body);
  };

  const resetState = () => {
    setAction('');
    setRoomType(null);
    setRoomTypes([]);
  };

  return {
    addRoomType,
    roomType,
    roomTypes,
    getRoomType,
    getRoomTypes,
    loading,
    updateRoomType,
  };
};

const mapApiRoomType = (roomType: CreateRoomType): ApiCreateRoomType => ({
  Active: roomType.active,
  Name: roomType.name,
});

export const mapRoomType = (roomType: ApiRoomType): RoomType => ({
  id: roomType.ID,
  active: roomType.Active,
  name: roomType.Name,
  numberOfRooms: roomType.NumberOfRooms,
});
